<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import AppLayout from '../layouts/AppLayout.vue'
import { fetchAdvisorSupplierProfile, type AdvisorSupplierProfile } from '../services/supplierApi'
import { useAuth } from '../composables/useAuth'
import { useRoute, useRouter } from 'vue-router'
import SupplierLogo from '../components/SupplierLogo.vue'
import { trackEvent } from '../services/analytics'
import { useSupplierPreferencesStore } from '../stores/supplierPreferences'
import { useContactPreferencesStore } from '../stores/contactPreferences'
import {
    fetchSupplierContactsForAdvisor,
    addPrivateContact,
    deletePrivateContact,
    reportStaleContact
} from '../services/api'

// Internal Metric Tile Component
import { h } from 'vue'
const MetricTile = (props: { label: string, value: string | number, sub?: string }) => {
    return h('div', { class: 'bg-cc-elevated p-4 rounded-lg border border-cc-border' }, [
        h('div', { class: 'text-xs font-semibold text-cc-text-secondary uppercase tracking-wider mb-1' }, props.label),
        h('div', { class: 'text-2xl font-bold text-cc-text' }, props.value),
        props.sub ? h('div', { class: 'text-xs text-gray-400 mt-1' }, props.sub) : null
    ])
}

const { getAccessTokenSilently } = useAuth()
const route = useRoute()
const router = useRouter()
const prefsStore = useSupplierPreferencesStore()
const contactPrefsStore = useContactPreferencesStore()

const supplier = ref<AdvisorSupplierProfile | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)

// Enhanced contacts
interface EnhancedContact {
    id: string
    name: string
    email: string
    role: string | null
    phone: string | null
    regions: string[]
    visibility: string
    is_private: boolean
    is_favorite: boolean
    notes: string | null
}
const enhancedContacts = ref<EnhancedContact[]>([])
const contactsLoading = ref(false)

// Add contact form
const showAddContactForm = ref(false)
const newContact = ref({ name: '', email: '', job_title: '', phone: '' })
const addingContact = ref(false)

// Report stale modal
const showReportModal = ref(false)
const reportTarget = ref<EnhancedContact | null>(null)
const reportReason = ref('')
const reportingStale = ref(false)

async function loadProfile() {
    loading.value = true
    try {
        const token = await getAccessTokenSilently()
        const id = route.params.supplierId as string
        supplier.value = await fetchAdvisorSupplierProfile(id, token)
        trackEvent('supplier_profile_viewed', {
            supplier_id: supplier.value?.id,
            supplier_name: supplier.value?.name,
        })
    } catch (e) {
        console.error(e)
        error.value = "Unable to load supplier profile."
    } finally {
        loading.value = false
    }
}

onMounted(async () => {
    await loadProfile()
    if (!prefsStore.loaded) prefsStore.load()
    if (!contactPrefsStore.loaded) contactPrefsStore.load()
    loadEnhancedContacts()
})

const formatCurrency = (val?: number) => {
    if (val === undefined) return 'N/A'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
}

const activeTab = ref('Overview')

watch(activeTab, (newTab) => {
    trackEvent('supplier_tab_changed', {
        tab: newTab,
        supplier_id: supplier.value?.id,
    })
})

function handleConnectRegister() {
    if (!supplier.value) return
    if (supplier.value.directQuoteUrl) {
        window.open(supplier.value.directQuoteUrl, '_blank')
        return
    }
    router.push({
        name: 'advisor-quote-create',
        query: {
            supplierId: supplier.value.id,
            supplierName: supplier.value.name
        }
    })
}

async function loadEnhancedContacts() {
    if (!supplier.value) return
    contactsLoading.value = true
    try {
        const token = await getAccessTokenSilently()
        const data = await fetchSupplierContactsForAdvisor(supplier.value.id, token)
        enhancedContacts.value = data.contacts
    } catch (e) {
        console.error('Failed to load enhanced contacts:', e)
    } finally {
        contactsLoading.value = false
    }
}

async function handleToggleContactFavorite(contactId: string) {
    await contactPrefsStore.toggleFavorite(contactId)
    // Reload contacts to reflect updated sort order
    await loadEnhancedContacts()
}

async function handleAddContact() {
    if (!supplier.value || !newContact.value.name || !newContact.value.email) return
    addingContact.value = true
    try {
        const token = await getAccessTokenSilently()
        await addPrivateContact({
            supplier_id: supplier.value.id,
            name: newContact.value.name,
            email: newContact.value.email,
            job_title: newContact.value.job_title || undefined,
            phone: newContact.value.phone || undefined
        }, token)
        newContact.value = { name: '', email: '', job_title: '', phone: '' }
        showAddContactForm.value = false
        await loadEnhancedContacts()
    } catch (e) {
        console.error('Failed to add contact:', e)
    } finally {
        addingContact.value = false
    }
}

async function handleDeletePrivateContact(contactId: string) {
    try {
        const token = await getAccessTokenSilently()
        await deletePrivateContact(contactId, token)
        await loadEnhancedContacts()
    } catch (e) {
        console.error('Failed to delete contact:', e)
    }
}

function openReportModal(contact: EnhancedContact) {
    reportTarget.value = contact
    reportReason.value = ''
    showReportModal.value = true
}

async function handleReportStale() {
    if (!reportTarget.value) return
    reportingStale.value = true
    try {
        const token = await getAccessTokenSilently()
        await reportStaleContact({
            supplier_employee_id: reportTarget.value.id,
            reason: reportReason.value || undefined
        }, token)
        showReportModal.value = false
        reportTarget.value = null
    } catch (e) {
        console.error('Failed to report stale contact:', e)
    } finally {
        reportingStale.value = false
    }
}
</script>

<template>
    <AppLayout>
        
        <div v-if="loading" class="animate-pulse space-y-8">
            <div class="h-32 bg-cc-elevated rounded-lg"></div>
            <div class="grid grid-cols-4 gap-4">
                <div class="h-24 bg-cc-elevated rounded-lg"></div>
                <div class="h-24 bg-cc-elevated rounded-lg"></div>
                <div class="h-24 bg-cc-elevated rounded-lg"></div>
                <div class="h-24 bg-cc-elevated rounded-lg"></div>
            </div>
        </div>

        <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 p-8 rounded-lg text-center">
            <h3 class="text-xl font-bold mb-2">Error</h3>
            <p>{{ error }}</p>
            <router-link to="/advisor/suppliers" class="inline-block mt-4 text-sm font-bold text-red-800 hover:underline">
                &larr; Back to Directory
            </router-link>
        </div>

        <div v-else-if="supplier" class="max-w-7xl mx-auto">
            
            <!-- Breadcrumbs -->
            <nav class="flex mb-6" aria-label="Breadcrumb">
                <ol class="inline-flex items-center space-x-1 md:space-x-3 text-sm">
                    <li class="inline-flex items-center">
                        <router-link to="/advisor/suppliers" class="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
                            Suppliers
                        </router-link>
                    </li>
                    <li>
                        <div class="flex items-center">
                            <span class="text-gray-400 mx-2">/</span>
                            <span class="text-cc-text font-medium">{{ supplier.name }}</span>
                        </div>
                    </li>
                </ol>
            </nav>

            <!-- Header -->
            <div class="bg-cc-elevated rounded-xl p-6 border border-cc-border shadow-sm mb-6 relative overflow-hidden">
                <div class="flex flex-col md:flex-row md:items-start justify-between gap-6">
                    <div class="flex items-start gap-6">
                        <div class="h-24 w-24 bg-white p-2 rounded-xl border border-gray-100 dark:border-gray-600 flex items-center justify-center shadow-sm shrink-0">
                            <SupplierLogo v-if="supplier?.logoUrl" :src="supplier.logoUrl" :alt="supplier?.name" class="object-contain max-h-full max-w-full" />
                            <span v-else class="text-2xl font-bold text-gray-400">{{ supplier.name.substring(0,2) }}</span>
                        </div>
                        <div>
                            <div class="flex flex-wrap items-center gap-3">
                                <h1 class="text-3xl font-bold text-cc-text">{{ supplier.name }}</h1>
                                <span v-if="supplier.promoBadge" class="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-0.5 rounded border border-green-200 uppercase tracking-wide">
                                    {{ supplier.promoBadge }}
                                </span>
                            </div>
                            <p class="text-lg text-cc-text-secondary mt-2 max-w-3xl">{{ supplier.shortDescription }}</p>
                            
                            <div class="flex flex-wrap gap-2 mt-4">
                                <span v-for="tag in supplier.tags" :key="tag" class="px-3 py-1 bg-cc-hover text-cc-text-secondary text-sm font-medium rounded-full">
                                    {{ tag }}
                                </span>
                            </div>
                        </div>
                    </div>
                     <div class="flex flex-col items-end gap-3 min-w-[260px] shrink-0">
                        <a v-if="supplier.websiteUrl" :href="supplier.websiteUrl" target="_blank" class="w-full text-center px-4 py-2 bg-white dark:bg-gray-700 border border-cc-border-med rounded-lg text-sm font-semibold text-cc-text hover:bg-gray-50 dark:hover:bg-gray-600 transition">
                            Visit Website ↗
                        </a>
                        <button
                            @click="handleConnectRegister"
                            class="w-full text-center px-4 py-2 bg-cc-primary hover:bg-cc-primary-hover text-white rounded-lg text-sm font-semibold transition shadow-sm"
                        >
                            Connect / Register
                        </button>
                        <!-- Preference Buttons -->
                        <div class="flex gap-2 w-full">
                            <button
                                @click="prefsStore.toggleFavorite(supplier.id)"
                                class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all border"
                                :class="prefsStore.isFavorite(supplier.id)
                                    ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700 hover:bg-yellow-100 dark:hover:bg-yellow-900/50'
                                    : 'bg-gray-50 dark:bg-gray-700 text-cc-text-secondary border-cc-border-med hover:bg-yellow-50 hover:text-yellow-600 hover:border-yellow-300 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-400 dark:hover:border-yellow-700'"
                            >
                                <svg class="w-4 h-4" :fill="prefsStore.isFavorite(supplier.id) ? 'currentColor' : 'none'" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                                </svg>
                                {{ prefsStore.isFavorite(supplier.id) ? 'Favorited' : 'Favorite' }}
                            </button>
                            <button
                                @click="prefsStore.toggleDirectContract(supplier.id)"
                                class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all border"
                                :class="prefsStore.isDirectContract(supplier.id)
                                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                                    : 'bg-gray-50 dark:bg-gray-700 text-cc-text-secondary border-cc-border-med hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 dark:hover:bg-blue-900/30 dark:hover:text-blue-400 dark:hover:border-blue-700'"
                            >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                                </svg>
                                Direct
                            </button>
                            <button
                                @click="prefsStore.toggleBlocked(supplier.id)"
                                class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all border"
                                :class="prefsStore.isBlocked(supplier.id)
                                    ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/50'
                                    : 'bg-gray-50 dark:bg-gray-700 text-cc-text-secondary border-cc-border-med hover:bg-red-50 hover:text-red-600 hover:border-red-300 dark:hover:bg-red-900/30 dark:hover:text-red-400 dark:hover:border-red-700'"
                            >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
                                </svg>
                                {{ prefsStore.isBlocked(supplier.id) ? 'Blocked' : 'Block' }}
                            </button>
                        </div>
                        <!-- TSD Affiliations -->
                        <div v-if="supplier.tsdAffiliations && supplier.tsdAffiliations.length > 0" class="flex flex-wrap gap-1.5 w-full">
                            <span v-for="tsd in supplier.tsdAffiliations" :key="tsd.id" class="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full border border-blue-200 dark:border-blue-800">
                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
                                </svg>
                                {{ tsd.name }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tabs -->
            <div class="flex border-b border-cc-border mb-6">
                <button 
                    v-for="tab in ['Overview', 'Products', 'Resources']" 
                    :key="tab"
                    @click="activeTab = tab"
                    class="px-6 py-3 text-sm font-medium border-b-2 transition-colors"
                    :class="activeTab === tab ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'"
                >
                    {{ tab }}
                </button>
            </div>

            <!-- Content -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                <!-- Main Content Area (Left 2/3) -->
                <div class="lg:col-span-2 space-y-8">
                    
                    <!-- Overview Tab -->
                    <div v-if="activeTab === 'Overview'" class="space-y-6 animate-fade-in-up">
                         <!-- Metrics -->
                        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <MetricTile label="Total Products" :value="supplier.metrics?.productCount ?? 0" />
                            <MetricTile label="Training Resources" :value="supplier.metrics?.resourceCount ?? 0" />
                            <MetricTile label="Closed Won (90d)" :value="supplier.metrics?.last90dClosedDeals || '-'" sub="Your deals" />
                            <MetricTile label="Est. MRR (90d)" :value="formatCurrency(supplier.metrics?.last90dMrr)" sub="Your production" />
                        </div>

                        <!-- Company Intelligence -->
                        <section v-if="supplier.employeeCount || supplier.foundedYear || supplier.channelInvestmentScore" class="bg-cc-elevated rounded-xl border border-cc-border p-5">
                            <h3 class="font-bold text-cc-text mb-4 flex items-center gap-2">
                                <svg class="w-5 h-5 text-cc-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                                Company Intelligence
                            </h3>
                            <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                <div v-if="supplier.employeeCount">
                                    <div class="text-xs text-cc-text-secondary uppercase font-semibold mb-0.5">Employees</div>
                                    <div class="text-lg font-bold text-cc-text">{{ supplier.employeeCount.toLocaleString() }}</div>
                                    <div v-if="supplier.employeeGrowthYoyPct != null" class="text-xs font-medium" :class="supplier.employeeGrowthYoyPct >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'">
                                        {{ supplier.employeeGrowthYoyPct >= 0 ? '+' : '' }}{{ supplier.employeeGrowthYoyPct }}% YoY
                                    </div>
                                </div>
                                <div v-if="supplier.annualRevenueRange">
                                    <div class="text-xs text-cc-text-secondary uppercase font-semibold mb-0.5">Revenue</div>
                                    <div class="text-lg font-bold text-cc-text">{{ supplier.annualRevenueRange }}</div>
                                </div>
                                <div v-if="supplier.foundedYear">
                                    <div class="text-xs text-cc-text-secondary uppercase font-semibold mb-0.5">Founded</div>
                                    <div class="text-lg font-bold text-cc-text">{{ supplier.foundedYear }}</div>
                                    <div class="text-xs text-cc-text-tertiary">{{ new Date().getFullYear() - supplier.foundedYear }} years</div>
                                </div>
                                <div v-if="supplier.headquartersCity">
                                    <div class="text-xs text-cc-text-secondary uppercase font-semibold mb-0.5">Headquarters</div>
                                    <div class="text-lg font-bold text-cc-text">{{ supplier.headquartersCity }}<span v-if="supplier.headquartersState">, {{ supplier.headquartersState }}</span></div>
                                </div>
                            </div>
                            <!-- Scores -->
                            <div v-if="supplier.channelInvestmentScore || supplier.productDepthScore || supplier.financialStabilityScore" class="flex flex-wrap gap-3 mb-4">
                                <div v-if="supplier.channelInvestmentScore" class="flex items-center gap-2 px-3 py-2 rounded-lg border border-cc-border">
                                    <div class="text-xs text-cc-text-secondary font-medium">Channel Investment</div>
                                    <div class="text-sm font-bold" :class="supplier.channelInvestmentScore >= 7 ? 'text-green-600 dark:text-green-400' : supplier.channelInvestmentScore >= 4 ? 'text-amber-600 dark:text-amber-400' : 'text-red-500'">
                                        {{ supplier.channelInvestmentScore }}/10
                                    </div>
                                </div>
                                <div v-if="supplier.productDepthScore" class="flex items-center gap-2 px-3 py-2 rounded-lg border border-cc-border">
                                    <div class="text-xs text-cc-text-secondary font-medium">Product Depth</div>
                                    <div class="text-sm font-bold" :class="supplier.productDepthScore >= 7 ? 'text-green-600 dark:text-green-400' : supplier.productDepthScore >= 4 ? 'text-amber-600 dark:text-amber-400' : 'text-red-500'">
                                        {{ supplier.productDepthScore }}/10
                                    </div>
                                </div>
                                <div v-if="supplier.financialStabilityScore" class="flex items-center gap-2 px-3 py-2 rounded-lg border border-cc-border">
                                    <div class="text-xs text-cc-text-secondary font-medium">Financial Stability</div>
                                    <div class="text-sm font-bold" :class="supplier.financialStabilityScore >= 7 ? 'text-green-600 dark:text-green-400' : supplier.financialStabilityScore >= 4 ? 'text-amber-600 dark:text-amber-400' : 'text-red-500'">
                                        {{ supplier.financialStabilityScore }}/10
                                    </div>
                                </div>
                            </div>
                            <!-- Additional details -->
                            <div class="flex flex-wrap gap-x-6 gap-y-2 text-xs text-cc-text-secondary">
                                <span v-if="supplier.ownershipType" class="font-medium">{{ supplier.ownershipType }}</span>
                                <span v-if="supplier.stockTicker">{{ supplier.stockTicker }}</span>
                                <span v-if="supplier.primarySegments && supplier.primarySegments.length > 0">Segments: {{ supplier.primarySegments.join(', ') }}</span>
                                <span v-if="supplier.primaryIndustries && supplier.primaryIndustries.length > 0">Industries: {{ supplier.primaryIndustries.join(', ') }}</span>
                                <span v-if="supplier.geographicCoverage && supplier.geographicCoverage.length > 0">Coverage: {{ supplier.geographicCoverage.join(', ') }}</span>
                                <a v-if="supplier.linkedinUrl" :href="supplier.linkedinUrl" target="_blank" class="text-cc-primary hover:underline">LinkedIn</a>
                            </div>
                        </section>

                        <!-- Featured Products Preview -->
                        <section>
                             <div class="flex items-center justify-between mb-4">
                                <h3 class="text-lg font-bold text-cc-text">Featured Products</h3>
                                <button @click="activeTab = 'Products'" class="text-sm text-blue-600 hover:underline">View All &rarr;</button>
                             </div>
                             <div class="grid sm:grid-cols-2 gap-4">
                                 <div v-for="product in (supplier.products || []).slice(0, 4)" :key="product.id" class="border border-cc-border p-4 rounded-lg bg-cc-elevated hover:shadow-sm transition">
                                     <div class="font-bold text-cc-text mb-1 truncate">{{ product.name }}</div>
                                     <div class="text-xs text-gray-500 line-clamp-2">{{ product.headline }}</div>
                                 </div>
                             </div>
                        </section>
                    </div>

                    <!-- Products Tab -->
                    <div v-if="activeTab === 'Products'" class="animate-fade-in-up">
                        <div class="bg-cc-elevated rounded-xl border border-cc-border overflow-hidden">
                            <div class="divide-y divide-cc-border">
                                    <div v-for="product in supplier.products" :key="product.id" class="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                                        <div class="flex justify-between items-start mb-2">
                                            <h3 class="font-bold text-cc-text text-lg">{{ product?.name }}</h3>
                                            <span class="text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded">
                                                {{ product.pricingBand || 'Standard' }}
                                            </span>
                                        </div>
                                        <p class="text-cc-text-secondary mb-3">{{ product.headline || 'No description available.' }}</p>
                                        <div class="flex gap-2">
                                            <span v-for="cat in product.categories" :key="cat" class="text-xs text-gray-500 border border-cc-border px-2 py-0.5 rounded">
                                                {{ cat }}
                                            </span>
                                        </div>
                                    </div>
                                    <div v-if="(supplier.products || []).length === 0" class="p-12 text-center text-gray-400">
                                        No products listed yet.
                                    </div>
                            </div>
                        </div>
                    </div>

                    <!-- Resources Tab -->
                    <div v-if="activeTab === 'Resources'" class="animate-fade-in-up">
                         <div class="bg-cc-elevated rounded-xl border border-cc-border overflow-hidden">
                            <div class="divide-y divide-cc-border">
                                <div v-for="asset in supplier.trainingAssets" :key="asset.id" class="p-4 flex items-center justify-between group hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                                    <div class="flex items-center gap-4">
                                        <div class="h-12 w-12 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg flex items-center justify-center font-bold text-xl">
                                            <svg v-if="asset.assetType === 'Video'" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg><svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
                                        </div>
                                        <div>
                                            <h4 class="font-bold text-cc-text group-hover:text-blue-600 transition">{{ asset.title }}</h4>
                                            <div class="text-sm text-cc-text-secondary">
                                                {{ asset.assetType }}
                                            </div>
                                        </div>
                                    </div>
                                    <a :href="asset.url" target="_blank" class="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 transition">
                                        View
                                    </a>
                                </div>
                                    <div v-if="(supplier.trainingAssets || []).length === 0" class="p-12 text-center text-gray-400">
                                        No training assets available.
                                    </div>
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Right Sidebar (Always Visible) -->
                <div class="space-y-6">
                    
                    <!-- Supplier Phone -->
                    <div v-if="supplier.contactPhone" class="bg-cc-elevated rounded-xl border border-cc-border p-5 shadow-sm">
                        <div class="text-xs font-semibold text-cc-text-secondary uppercase tracking-wider mb-2">Main Phone</div>
                        <a :href="`tel:${supplier.contactPhone}`" class="text-lg font-bold text-blue-600 hover:underline flex items-center gap-2">
                            <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg> {{ supplier.contactPhone }}
                        </a>
                    </div>

                    <!-- Contacts (Enhanced) -->
                    <section class="bg-cc-elevated rounded-xl border border-cc-border overflow-hidden shadow-sm">
                        <div class="px-6 py-4 border-b border-cc-border bg-cc-deep flex items-center justify-between">
                            <h2 class="font-bold text-cc-text">Channel Contacts</h2>
                            <button @click="showAddContactForm = !showAddContactForm" class="text-xs text-blue-600 hover:text-blue-700 font-medium">
                                {{ showAddContactForm ? 'Cancel' : '+ Add Contact' }}
                            </button>
                        </div>

                        <!-- Add Contact Form -->
                        <div v-if="showAddContactForm" class="p-4 border-b border-cc-border bg-blue-50/50 dark:bg-blue-900/10">
                            <div class="space-y-2">
                                <input v-model="newContact.name" type="text" placeholder="Name *" class="w-full px-3 py-1.5 text-sm border border-cc-border rounded-lg bg-white dark:bg-gray-800 text-cc-text" />
                                <input v-model="newContact.email" type="email" placeholder="Email *" class="w-full px-3 py-1.5 text-sm border border-cc-border rounded-lg bg-white dark:bg-gray-800 text-cc-text" />
                                <input v-model="newContact.job_title" type="text" placeholder="Role/Title" class="w-full px-3 py-1.5 text-sm border border-cc-border rounded-lg bg-white dark:bg-gray-800 text-cc-text" />
                                <input v-model="newContact.phone" type="text" placeholder="Phone" class="w-full px-3 py-1.5 text-sm border border-cc-border rounded-lg bg-white dark:bg-gray-800 text-cc-text" />
                                <button
                                    @click="handleAddContact"
                                    :disabled="addingContact || !newContact.name || !newContact.email"
                                    class="w-full px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition"
                                >
                                    {{ addingContact ? 'Adding...' : 'Add Private Contact' }}
                                </button>
                            </div>
                        </div>

                        <div v-if="enhancedContacts.length > 0" class="divide-y divide-cc-border">
                            <div v-for="contact in enhancedContacts" :key="contact.id" class="p-4 group">
                                <div class="flex items-start justify-between">
                                    <div class="flex-1 min-w-0">
                                        <div class="flex items-center gap-2">
                                            <span class="font-bold text-cc-text text-sm truncate">{{ contact.name }}</span>
                                            <span v-if="contact.is_private" class="text-[10px] font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded">Added by you</span>
                                        </div>
                                        <div class="text-xs text-cc-text-secondary mb-2">{{ contact.role || 'No title' }}</div>
                                    </div>
                                    <div class="flex items-center gap-1 shrink-0">
                                        <!-- Favorite star -->
                                        <button @click="handleToggleContactFavorite(contact.id)" class="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition" :title="contact.is_favorite ? 'Unfavorite' : 'Favorite'">
                                            <svg class="w-4 h-4" :class="contact.is_favorite ? 'text-yellow-500' : 'text-gray-300 dark:text-gray-600'" :fill="contact.is_favorite ? 'currentColor' : 'none'" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                                            </svg>
                                        </button>
                                        <!-- Report stale (only for public contacts) -->
                                        <button v-if="!contact.is_private" @click="openReportModal(contact)" class="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition opacity-0 group-hover:opacity-100" title="Report as stale">
                                            <svg class="w-4 h-4 text-gray-400 hover:text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                            </svg>
                                        </button>
                                        <!-- Delete (only for private contacts) -->
                                        <button v-if="contact.is_private" @click="handleDeletePrivateContact(contact.id)" class="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition opacity-0 group-hover:opacity-100" title="Delete contact">
                                            <svg class="w-4 h-4 text-gray-400 hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <a :href="`mailto:${contact.email}`" class="text-xs text-blue-600 hover:underline flex items-center gap-1.5 break-all">
                                    <svg class="w-3.5 h-3.5 inline shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg> {{ contact.email }}
                                </a>
                                <a v-if="contact.phone" :href="`tel:${contact.phone}`" class="text-xs text-blue-600 hover:underline flex items-center gap-1.5 mt-1">
                                    <svg class="w-3.5 h-3.5 inline shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg> {{ contact.phone }}
                                </a>
                            </div>
                        </div>
                        <div v-else-if="!contactsLoading" class="p-6 text-sm text-gray-400 text-center">
                            No channel contacts listed.
                        </div>
                        <div v-else class="p-6 text-sm text-gray-400 text-center animate-pulse">
                            Loading contacts...
                        </div>
                    </section>

                    <!-- Report Stale Contact Modal -->
                    <div v-if="showReportModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" @click.self="showReportModal = false">
                        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
                            <h3 class="text-lg font-bold text-cc-text mb-2">Report Stale Contact</h3>
                            <p class="text-sm text-cc-text-secondary mb-4">
                                Is <strong>{{ reportTarget?.name }}</strong> still at this company?
                            </p>
                            <textarea
                                v-model="reportReason"
                                placeholder="Optional: Why do you think this contact is stale? (e.g., 'Left the company', 'Moved to a different role')"
                                class="w-full px-3 py-2 text-sm border border-cc-border rounded-lg bg-white dark:bg-gray-700 text-cc-text resize-none"
                                rows="3"
                            ></textarea>
                            <div class="flex justify-end gap-2 mt-4">
                                <button @click="showReportModal = false" class="px-4 py-2 text-sm font-medium text-cc-text-secondary hover:text-cc-text transition">
                                    Cancel
                                </button>
                                <button
                                    @click="handleReportStale"
                                    :disabled="reportingStale"
                                    class="px-4 py-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-50 transition"
                                >
                                    {{ reportingStale ? 'Reporting...' : 'Report as Stale' }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Primary Categories / Vertical Focus - Only shown when data is available -->
                    <section v-if="supplier.primaryCategories && supplier.primaryCategories.length > 0" class="bg-cc-elevated rounded-xl border border-cc-border p-6 shadow-sm">
                         <h3 class="font-bold text-cc-text mb-3 text-sm uppercase tracking-wide">Primary Categories</h3>
                         <div class="flex flex-wrap gap-2">
                             <span v-for="category in supplier.primaryCategories" :key="category" class="text-xs bg-cc-hover text-cc-text-secondary px-2 py-1 rounded">{{ category }}</span>
                         </div>
                    </section>

                </div>

            </div>

        </div>
    </AppLayout>
</template>
